import { NEWS_LIST } from '@/apollo/queries/news'
import {
  NewsListQuery,
  NewsListQueryVariables,
  OrderEnum,
  PostObjectsConnectionOrderbyEnum,
} from '@/apollo/types'
import { BackBlock } from '@/components/back-block'
import { Container } from '@/components/container'
import { InViewFadeIn } from '@/components/fade-in'
import { PageHeader } from '@/components/page-header'
import { Pagination } from '@/components/pagination'
import { useLazyQuery } from '@apollo/client'
import { css, useTheme } from '@emotion/react'
import { Link } from 'gatsby'
import React, { useEffect, useState } from 'react'
import { formatDate } from '@/helpers/format-date'

const ALL_CATEGORY = 'すべて'

export default function NewsList() {
  const theme = useTheme()

  const [pageInfo, setPageInfo] = useState({
    currentPage: 0,
    category: ALL_CATEGORY,
  })
  const { currentPage, category } = pageInfo

  const [getNewsList, { data, previousData, loading }] = useLazyQuery<
    NewsListQuery,
    NewsListQueryVariables
  >(NEWS_LIST)

  const paging = (index: number) => {
    setPageInfo(prev => ({
      ...prev,
      currentPage: index,
    }))
  }

  useEffect(() => {
    getNewsList({
      variables: {
        where: {
          ...{
            categoryName: category === ALL_CATEGORY ? undefined : category,
            offsetPagination: { offset: currentPage * 8, size: 8 },
            orderby: [
              {
                field: PostObjectsConnectionOrderbyEnum.Date,
                order: OrderEnum.Desc,
              },
            ],
          },
        },
      },
    })
  }, [currentPage, category])

  if (loading) {
    return (
      <div
        css={css`
          position: fixed;
          top: 0;
          left: 0;
          z-index: 1000;
          display: flex;
          justify-content: center;
          align-items: center;
          width: 100vw;
          height: 100vh;
          background-color: ${theme.colors.white};
        `}
      >
        <h1>Loading...</h1>
      </div>
    )
  }

  return (
    <section
      id="news-event"
      css={css`
        padding-bottom: 18rem;
      `}
    >
      <PageHeader underlineColor="magenta">新着情報</PageHeader>

      <div
        css={css`
          padding: 6rem 4rem 4rem;
        `}
      >
        <Container>
          <InViewFadeIn
            css={css`
              position: relative;

              &::after {
                content: '';
                position: absolute;
                top: 0;
                right: -4rem;
                width: 8rem;
                height: 100%;
                background: linear-gradient(
                  to right,
                  rgba(255, 255, 255, 0) 0%,
                  #fff 90%
                );
              }
            `}
          >
            <div
              css={css`
                display: flex;
                justify-content: center;
                gap: 1rem;
                width: calc(100% + 4rem);
                overflow: scroll;

                & > button {
                  position: relative;
                  left: -2rem;
                  flex-shrink: 0;
                  padding: 1rem 1.5rem;
                  font-size: ${theme.fontSizes.body5};
                  background-color: ${theme.colors.gray.lighter};
                  border-radius: 9999px;

                  &[data-name=${pageInfo.category}] {
                    filter: brightness(0.9);
                  }

                  @media ${theme.breakpoints.md} {
                    transition: filter 0.3s ease-out;

                    &:hover {
                      filter: brightness(0.9);
                    }
                  }
                }
              `}
            >
              <button
                data-name={ALL_CATEGORY}
                onClick={() => {
                  setPageInfo({
                    currentPage: 0,
                    category: ALL_CATEGORY,
                  })
                }}
              >
                すべて
              </button>

              {(
                data?.categories?.nodes ?? previousData?.categories?.nodes
              )?.map(node => {
                const { id, name } = node ?? {}
                return (
                  <button
                    key={id}
                    data-name={name}
                    onClick={() => {
                      if (name) {
                        setPageInfo({
                          currentPage: 0,
                          category: name,
                        })
                      }
                    }}
                  >
                    {name}
                  </button>
                )
              })}
            </div>
          </InViewFadeIn>

          <InViewFadeIn>
            <div
              css={css`
                display: grid;
                grid-template-columns: 11.4rem 1fr 3rem;
                align-items: center;
                gap: 3rem 2rem;
                margin-top: 12rem;

                @media ${theme.breakpoints.md} {
                  grid-template-columns: 13.4rem 1fr 3rem;
                  gap: 3rem 4rem;
                  margin-top: 17rem;
                }
              `}
            >
              {(data?.newsList?.nodes ?? previousData?.newsList?.nodes)?.map(
                node => {
                  const { id, title, date } = node ?? {}
                  const uri = `/news?id=${id ?? ''}`
                  return (
                    <div
                      key={id}
                      css={css`
                        display: contents;
                      `}
                    >
                      <Link
                        to={uri}
                        css={css`
                          display: block;
                          color: ${theme.colors.magenta.main};
                          font-size: ${theme.fontSizes.body3};
                          font-family: ${theme.fonts.roboto};
                          font-weight: ${theme.fontWeights.regular};
                          letter-spacing: ${theme.letterSpacings.tight};

                          @media ${theme.breakpoints.md} {
                            font-size: ${theme.fontSizes.body1};
                          }
                        `}
                      >
                        {formatDate(date ?? '')}
                      </Link>

                      <Link
                        to={uri}
                        css={css`
                          display: block;
                          letter-spacing: ${theme.letterSpacings.wide};
                          width: 100%;
                          white-space: nowrap;
                          text-overflow: ellipsis;
                          overflow: hidden;
                          transition: color 0.25s ease-out;

                          @media ${theme.breakpoints.md} {
                            &:hover {
                              color: ${theme.colors.magenta.main};
                              text-decoration: underline;
                            }
                          }
                        `}
                      >
                        {title}
                      </Link>

                      <Link
                        to={uri}
                        css={css`
                          display: block;
                        `}
                      >
                        <span
                          className="icon-angle-right"
                          css={css`
                            font-size: 1.4rem;
                            color: ${theme.colors.magenta.main};
                          `}
                        />
                      </Link>

                      <div
                        css={css`
                          grid-column: span 3;
                          width: 100%;
                          height: 1px;
                          background-color: ${theme.colors.gray.light};
                        `}
                      />
                    </div>
                  )
                }
              )}
            </div>
          </InViewFadeIn>

          <InViewFadeIn>
            <Pagination
              total={
                data?.newsList?.pageInfo?.offsetPagination?.total ??
                previousData?.newsList?.pageInfo?.offsetPagination?.total ??
                1
              }
              currentPage={currentPage}
              onPageTo={paging}
            />
          </InViewFadeIn>
        </Container>
      </div>

      <BackBlock />
    </section>
  )
}
